import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Meter, Reading, ApiResponse } from '@/types'
import { invalidateDetailQueries, removeListQueries } from '@/lib/queryHelpers'

// Query keys
export const meterKeys = {
  all: ['meters'] as const,
  lists: () => [...meterKeys.all, 'list'] as const,
  list: (unitId: number) => [...meterKeys.lists(), unitId] as const,
  details: () => [...meterKeys.all, 'detail'] as const,
  detail: (id: number) => [...meterKeys.details(), id] as const,
}

export const readingKeys = {
  all: ['readings'] as const,
  lists: () => [...readingKeys.all, 'list'] as const,
  list: (meterId: number) => [...readingKeys.lists(), meterId] as const,
}

// Fetch meters for a unit
export function useMeters(unitId: number) {
  return useQuery({
    queryKey: meterKeys.list(unitId),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Meter[]>>(`/api/units/${unitId}/meters`)
      return data.data
    },
    enabled: !!unitId,
  })
}

// Fetch single meter
export function useMeter(id: number) {
  return useQuery({
    queryKey: meterKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Meter>>(`/api/meters/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create meter
interface CreateMeterInput {
  unitId: number
  meter_type: 'water' | 'electricity'
  serial_number?: string
}

export function useCreateMeter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ unitId, ...input }: CreateMeterInput) => {
      const { data } = await api.post<ApiResponse<Meter>>(`/api/units/${unitId}/meters`, {
        meter: input,
      })
      return { data: data.data, unitId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: meterKeys.list(result.unitId) })
    },
  })
}

// Update meter
interface UpdateMeterInput {
  id: number
  unitId: number
  serial_number?: string
}

export function useUpdateMeter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, unitId, ...input }: UpdateMeterInput) => {
      const { data } = await api.patch<ApiResponse<Meter>>(`/api/meters/${id}`, {
        meter: input,
      })
      return { data: data.data, unitId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: meterKeys.list(result.unitId) })
      invalidateDetailQueries(queryClient, meterKeys.detail(result.data.id))
    },
  })
}

// Delete meter
export function useDeleteMeter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, unitId }: { id: number; unitId: number }) => {
      await api.delete(`/api/meters/${id}`)
      return { id, unitId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: meterKeys.list(result.unitId) })
    },
  })
}

// Fetch readings for a meter
export function useReadings(meterId: number) {
  return useQuery({
    queryKey: readingKeys.list(meterId),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Reading[]>>(`/api/meters/${meterId}/readings`)
      return data.data
    },
    enabled: !!meterId,
  })
}

// Create reading
interface CreateReadingInput {
  meterId: number
  previous_value: number
  current_value: number
  read_at: string
  image_urls?: string[]
  confirmed_rollover?: boolean
}

// Callers opting out of the global toast must surface every failure locally.
interface ReadingMutationOptions {
  suppressErrorToast?: boolean
}

export function useCreateReading(options: ReadingMutationOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    meta: { suppressErrorToast: options.suppressErrorToast },
    mutationFn: async ({ meterId, ...input }: CreateReadingInput) => {
      const { data } = await api.post<ApiResponse<Reading>>(`/api/meters/${meterId}/readings`, {
        reading: input,
      })
      return { data: data.data, meterId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: readingKeys.list(result.meterId) })
      invalidateDetailQueries(queryClient, meterKeys.detail(result.meterId))
      // BulkReadingsPage uses usePropertyReadings(['property-readings', propertyId, month])
      // to render the month's reading per meter — refresh those caches too. Broad
      // invalidation by key prefix since we don't know propertyId/month here.
      queryClient.invalidateQueries({ queryKey: ['property-readings'] })
    },
  })
}

// Update reading
interface UpdateReadingInput {
  id: number
  meterId: number
  previous_value?: number
  current_value: number
  read_at: string
  image_urls?: string[]
  confirmed_rollover?: boolean
}

export function useUpdateReading(options: ReadingMutationOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    meta: { suppressErrorToast: options.suppressErrorToast },
    mutationFn: async ({ id, meterId, ...input }: UpdateReadingInput) => {
      const { data } = await api.patch<ApiResponse<Reading>>(`/api/readings/${id}`, {
        reading: input,
      })
      return { data: data.data, meterId }
    },
    onSuccess: (result) => {
      removeListQueries(queryClient, readingKeys.list(result.meterId))
      invalidateDetailQueries(queryClient, meterKeys.detail(result.meterId))
      queryClient.invalidateQueries({ queryKey: ['property-readings'] })
    },
  })
}

// Delete reading
export function useDeleteReading() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, meterId }: { id: number; meterId: number }) => {
      await api.delete(`/api/readings/${id}`)
      return { id, meterId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: readingKeys.list(result.meterId) })
      queryClient.invalidateQueries({ queryKey: ['property-readings'] })
    },
  })
}
